/**
 * Watchdog — error counter + auto-recovery.
 *
 * Three independent recovery layers, in order of escalation:
 *
 * 1. Sliding error-burst window. If MAX_ERRORS_BEFORE_RELOAD errors
 *    fire in ERROR_WINDOW_MS, we reload. Catches "everything is on
 *    fire" scenarios — e.g. plan-fetch failing while we render and
 *    the React error boundary cycles.
 *
 * 2. Boot-health check. Every start() runs `markBootStarted()`; once
 *    the player successfully gets to a stable state (boot finished,
 *    first plan applied or pairing screen up) it calls
 *    `markBootSucceeded()`. If the boot watchdog times out before that
 *    happens, we increment a localStorage counter and reload. After
 *    MAX_CONSECUTIVE_BOOT_FAILURES failures, we open the safe screen
 *    instead of looping forever — a human can then triple-tap into
 *    the diagnostic / factory-reset flow.
 *
 * 3. Reload-loop dampening. We keep a sliding window of recent reload
 *    timestamps in localStorage. If we'd be reloading more than
 *    MAX_RELOADS_PER_WINDOW times within RELOAD_WINDOW_MS, the safe
 *    screen takes over instead — same as boot-failure exhaustion.
 *
 * Recovery action when the watchdog fires:
 *   - Tizen: `platform.application.exit()` — runtime relaunches.
 *   - Web/SSSP: `window.location.reload()`.
 *
 * NOTE: this file deliberately uses raw `console.*` and NOT the logger.
 * The logger sometimes can be the source of failures itself (network
 * down, server 500, ...). If we routed watchdog through it we'd risk a
 * recursive log-error → reportError → flush → fail → reportError loop.
 * Watchdog stays minimal-dependency on purpose — only `platform`.
 */

import { platform } from '@/platform';

// ── Tunables ────────────────────────────────────────────────────────

const MAX_ERRORS_BEFORE_RELOAD = 50;
const ERROR_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

/** Time to reach a "stable state" after start() before boot is declared failed. */
const BOOT_TIMEOUT_MS = 60_000; // 60s — gives slow Tizen filesystems time

/** Show the safe screen after N consecutive boot failures. */
const MAX_CONSECUTIVE_BOOT_FAILURES = 3;

const MAX_RELOADS_PER_WINDOW = 4;
const RELOAD_WINDOW_MS = 10 * 60 * 1000; // 10 min

// localStorage keys. Hand-rolled (not via storage-keys.ts) so the
// watchdog can run before that module finishes loading.
const BOOT_FAILS_KEY = 'sensar_boot_failures';
const RELOAD_LOG_KEY = 'sensar_reload_log';
const SAFE_MODE_KEY = 'sensar_safe_mode';

// ── Error burst counter ────────────────────────────────────────────

const recentErrors: number[] = [];
let totalErrorsLifetime = 0;

export function reportError(context: string, err: unknown): void {
  const now = Date.now();
  totalErrorsLifetime++;
  recentErrors.push(now);

  while (recentErrors.length > 0 && recentErrors[0] < now - ERROR_WINDOW_MS) {
    recentErrors.shift();
  }

  console.error(
    `[Watchdog] Error in ${context}:`,
    err,
    `(${recentErrors.length}/${MAX_ERRORS_BEFORE_RELOAD} in window, ${totalErrorsLifetime} lifetime)`,
  );

  if (recentErrors.length >= MAX_ERRORS_BEFORE_RELOAD) {
    console.warn(
      `[Watchdog] Error threshold exceeded — restarting app (${MAX_ERRORS_BEFORE_RELOAD} errors in last ${
        ERROR_WINDOW_MS / 1000
      }s)`,
    );
    recentErrors.length = 0;
    triggerReload('error-burst');
  }
}

export function resetErrorCount(): void {
  recentErrors.length = 0;
}

export function getErrorCount(): number {
  return recentErrors.length;
}

export function getLifetimeErrorCount(): number {
  return totalErrorsLifetime;
}

// ── Boot health watchdog ────────────────────────────────────────────

let bootTimer: ReturnType<typeof setTimeout> | null = null;
let bootMarkedSuccess = false;

/**
 * Call at the very start of bundle-entry. Arms a timer that, if not
 * cleared by `markBootSucceeded()`, treats the current attempt as a
 * boot failure.
 */
export function markBootStarted(): void {
  bootMarkedSuccess = false;
  if (bootTimer) clearTimeout(bootTimer);
  bootTimer = setTimeout(() => {
    if (bootMarkedSuccess) return;
    console.warn('[Watchdog] Boot did not stabilise within timeout');
    handleBootFailure();
  }, BOOT_TIMEOUT_MS);
}

/**
 * Call once the React tree has actually mounted AND the first user-
 * facing screen (PlayerScreen or ProvisionScreen) is visible. Clears
 * the boot timer and resets the consecutive-failure counter.
 */
export function markBootSucceeded(): void {
  if (bootMarkedSuccess) return;
  bootMarkedSuccess = true;
  if (bootTimer) {
    clearTimeout(bootTimer);
    bootTimer = null;
  }
  // Successful boot clears the failure streak so the next genuine
  // failure starts from zero.
  try {
    localStorage.removeItem(BOOT_FAILS_KEY);
  } catch {
    // localStorage can throw in private-browse contexts — non-fatal.
  }
}

function handleBootFailure(): void {
  const fails = readBootFailures() + 1;
  writeBootFailures(fails);
  console.warn(`[Watchdog] Boot failure ${fails}/${MAX_CONSECUTIVE_BOOT_FAILURES}`);
  if (fails >= MAX_CONSECUTIVE_BOOT_FAILURES) {
    enterSafeMode('boot-failure-exhausted');
    return;
  }
  triggerReload('boot-timeout');
}

function readBootFailures(): number {
  try {
    const v = localStorage.getItem(BOOT_FAILS_KEY);
    return v ? parseInt(v, 10) || 0 : 0;
  } catch {
    return 0;
  }
}

function writeBootFailures(n: number): void {
  try {
    localStorage.setItem(BOOT_FAILS_KEY, String(n));
  } catch {
    // ignore
  }
}

// ── Reload-loop dampener ────────────────────────────────────────────

function triggerReload(reason: string): void {
  const now = Date.now();
  const log = readReloadLog().filter((t) => now - t < RELOAD_WINDOW_MS);
  log.push(now);
  writeReloadLog(log);

  if (log.length > MAX_RELOADS_PER_WINDOW) {
    console.warn(
      `[Watchdog] Reload storm detected (${log.length} in ${RELOAD_WINDOW_MS / 60000}min) — entering safe mode`,
    );
    enterSafeMode('reload-storm');
    return;
  }

  console.warn(`[Watchdog] Reloading (reason=${reason})`);
  if (typeof window === 'undefined') return;
  try {
    platform.application.exit();
  } catch {
    window.location.reload();
  }
}

function readReloadLog(): number[] {
  try {
    const v = localStorage.getItem(RELOAD_LOG_KEY);
    if (!v) return [];
    const arr = JSON.parse(v);
    return Array.isArray(arr) ? arr.filter((n) => typeof n === 'number') : [];
  } catch {
    return [];
  }
}

function writeReloadLog(log: number[]): void {
  try {
    localStorage.setItem(RELOAD_LOG_KEY, JSON.stringify(log));
  } catch {
    // ignore
  }
}

// ── Safe mode ───────────────────────────────────────────────────────

/**
 * Flag the localStorage entry that tells <App /> to show the safe
 * screen on next mount. We then reload one more time so the safe
 * screen actually renders — but the safe screen is plain DOM, no
 * React state hydration, no plan fetch, so it can never itself fail.
 */
function enterSafeMode(reason: string): void {
  try {
    localStorage.setItem(SAFE_MODE_KEY, JSON.stringify({ reason, ts: Date.now() }));
  } catch {
    // ignore
  }
  if (typeof window === 'undefined') return;
  try {
    window.location.reload();
  } catch {
    // last resort — render the safe screen directly into body.
    renderInlineSafeScreen(reason);
  }
}

export interface SafeModeFlag {
  reason: string;
  ts: number;
}

export function readSafeMode(): SafeModeFlag | null {
  try {
    const v = localStorage.getItem(SAFE_MODE_KEY);
    if (!v) return null;
    return JSON.parse(v) as SafeModeFlag;
  } catch {
    return null;
  }
}

/**
 * Clear safe mode. Called from the safe screen's "Try again" button.
 * Also resets the boot-failure counter so we don't immediately bounce
 * back into safe mode on the next attempt.
 */
export function clearSafeMode(): void {
  try {
    localStorage.removeItem(SAFE_MODE_KEY);
    localStorage.removeItem(BOOT_FAILS_KEY);
    localStorage.removeItem(RELOAD_LOG_KEY);
  } catch {
    // ignore
  }
}

function renderInlineSafeScreen(reason: string): void {
  if (typeof document === 'undefined') return;
  const root = document.getElementById('root');
  if (!root) return;
  root.innerHTML = `
    <div style="position:fixed;inset:0;display:flex;align-items:center;justify-content:center;
                background:#0A0A0A;color:#FF6B2B;font-family:Inter,system-ui,sans-serif;
                font-size:24px;flex-direction:column;gap:24px">
      <div>SENSAR PLAYER · SAFE MODE</div>
      <div style="font-size:14px;color:#888">reason: ${reason}</div>
    </div>
  `;
}
